import { CircleAlert, Info, Plus, RefreshCw, Rss, Trash2, Upload } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import type { Category, PlaceRule, Source } from '@tempo/core';
import { api } from '../api';
import { Button, ColorPicker, Field, Loading, Modal, NumberInput } from '../components/ui';
import { relative } from '../lib/format';
import { useAction, usePlaces, useSources } from '../lib/hooks';
import { toast } from '../lib/toast';

const CATEGORY_LABEL: Record<Category, string> = { work: 'Work', uni: 'Uni', personal: 'Personal', other: 'Other' };
const KIND_LABEL: Record<Source['kind'], string> = { ics: 'Calendar feed', manual: 'Added in Tempo', bridge: 'School Manager' };

export function CalendarsPage() {
  const sources = useSources();
  const [editing, setEditing] = useState<Source | 'new' | null>(null);
  return (
    <div className="page">
      <div className="page-head">
        <h1 className="grow">Calendars</h1>
        <Button variant="primary" onClick={() => setEditing('new')}>
          <Rss size={16} /> Add a feed
        </Button>
      </div>

      <div className="callout" style={{ marginBottom: 16 }}>
        <Info size={16} />
        <div className="stack tight">
          <div>
            <b>Uni timetable (Southampton):</b> sign in to SUSSED → <i>Timetable</i> → click <i>"get your calendar feed now"</i> → copy the link → edit
            "Uni timetable" below and paste it. It re-syncs every hour.
          </div>
          <div>
            <b>Wetherspoons shifts:</b> if your rota app offers a calendar / iCal link, paste it as a feed. Otherwise tap <b>+ → Shifts</b> and paste the
            rota text – Tempo reads the dates and times for you.
          </div>
        </div>
      </div>

      {sources.isLoading ? (
        <Loading />
      ) : (
        <div className="stack">
          {sources.data?.map((s) => (
            <SourceCard key={s.id} s={s} onEdit={() => setEditing(s)} />
          ))}
        </div>
      )}

      <PlaceRules />
      {editing && <SourceForm existing={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function SourceCard({ s, onEdit }: { s: Source; onEdit: () => void }) {
  const places = usePlaces().data ?? [];
  const sync = useAction(() => api.post<{ total: number }>(`/sources/${s.id}/sync`), (r) => `Synced – ${r.total} events`);
  const toggle = useAction((enabled: boolean) => api.patch(`/sources/${s.id}`, { enabled }));
  const fileRef = useRef<HTMLInputElement>(null);
  const importIcs = useAction((ics: string) => api.post<{ total: number }>(`/sources/${s.id}/import`, { ics }), (r) => `Imported ${r.total} events`);
  const place = places.find((p) => p.id === s.defaultPlaceId);

  return (
    <div className="card list-card">
      <div className="accent-bar" style={{ background: s.color }} />
      <div className="grow">
        <div className="row wrap" style={{ gap: 8 }}>
          <span className="title">{s.name}</span>
          <span className="badge">{CATEGORY_LABEL[s.category]}</span>
          <span className="badge">{KIND_LABEL[s.kind]}</span>
        </div>
        <div className="meta">
          {s.kind === 'ics' && !s.url && <span style={{ color: 'var(--warn)' }}>No feed link yet – edit to add it</span>}
          {s.lastSyncedAt && !s.lastError && (
            <span>
              Synced {relative(s.lastSyncedAt)}
              {s.lastEventCount != null ? ` · ${s.lastEventCount} events` : ''}
            </span>
          )}
          {place && <span>· at {place.name}</span>}
          {s.bufferBeforeMin > 0 && <span>· arrive {s.bufferBeforeMin} min early</span>}
        </div>
        {s.lastError && (
          <div className="small" style={{ color: 'var(--danger)', marginTop: 6, display: 'flex', gap: 6 }}>
            <CircleAlert size={14} style={{ marginTop: 2 }} /> {s.lastError}
          </div>
        )}
      </div>
      <div className="actions">
        {s.kind === 'ics' && s.url && (
          <Button size="sm" onClick={() => sync.mutate()} loading={sync.isPending}>
            <RefreshCw size={14} /> Sync
          </Button>
        )}
        {s.kind === 'manual' && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".ics,text/calendar"
              hidden
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                if (f.size > 4_000_000) return toast('That file is too big', 'error');
                importIcs.mutate(await f.text());
                e.target.value = '';
              }}
            />
            <Button size="sm" onClick={() => fileRef.current?.click()} title="Import an .ics file">
              <Upload size={14} /> Import
            </Button>
          </>
        )}
        {s.kind !== 'bridge' && (
          <Button size="sm" variant="ghost" onClick={onEdit}>
            Edit
          </Button>
        )}
        <button className={`switch ${s.enabled ? 'on' : ''}`} onClick={() => toggle.mutate(!s.enabled)} aria-label="Show this calendar" style={{ alignSelf: 'center' }} />
      </div>
    </div>
  );
}

function SourceForm({ existing, onClose }: { existing?: Source; onClose: () => void }) {
  const places = usePlaces().data ?? [];
  const [name, setName] = useState(existing?.name ?? '');
  const [url, setUrl] = useState(existing?.url ?? '');
  const [category, setCategory] = useState<Category>(existing?.category ?? 'uni');
  const [placeId, setPlaceId] = useState(existing?.defaultPlaceId ?? '');
  const [buffer, setBuffer] = useState<number | null>(existing?.bufferBeforeMin ?? 0);
  const [interval, setInterval] = useState<number | null>(existing?.syncIntervalMin ?? 60);
  const [color, setColor] = useState(existing?.color ?? '#3b82f6');
  const [template, setTemplate] = useState(existing?.titleTemplate ?? '');
  const isFeed = !existing || existing.kind === 'ics';
  const isManual = existing?.kind === 'manual';
  const willBecomeFeed = isManual && url.trim() !== '';

  const body = () => ({
    name: name.trim(),
    category,
    color,
    defaultPlaceId: placeId || null,
    bufferBeforeMin: buffer ?? 0,
    titleTemplate: template.trim() || null,
    ...(isFeed || willBecomeFeed ? { url: url.trim() || null, syncIntervalMin: interval ?? 60 } : {}),
  });
  const save = useAction(
    () => (existing ? api.patch(`/sources/${existing.id}`, body()) : api.post('/sources', { ...body(), kind: 'ics' })),
    existing && !willBecomeFeed ? 'Saved' : 'Saved – syncing',
  );
  const remove = useAction(() => api.del(`/sources/${existing!.id}`), 'Calendar removed');

  return (
    <Modal
      title={existing ? `Edit ${existing.name}` : 'Add a calendar feed'}
      onClose={onClose}
      footer={
        <>
          {existing && (
            <Button className="left" variant="danger" onClick={() => confirm(`Remove "${existing.name}" and all its events?`) && remove.mutateAsync().then(onClose).catch(() => undefined)}>
              <Trash2 size={16} /> Remove
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!name.trim() || (isFeed && !existing && !url.trim())} loading={save.isPending} onClick={() => save.mutateAsync().then(onClose).catch(() => undefined)}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Name">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Uni timetable" />
        </Field>
        {(isFeed || isManual) && (
          <Field
            label="Feed link (iCal / ICS / webcal)"
            hint={
              isManual
                ? 'Optional. Paste a calendar link (e.g. your mySchedule rota link) to sync this calendar automatically – it then replaces shifts you pasted by hand.'
                : 'Kept private on your server. Tempo fetches it on a schedule.'
            }
          >
            <input className="input mono" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… or webcal://…" />
          </Field>
        )}
        <div className="grid-2">
          <Field label="Type">
            <select className="input" value={category} onChange={(e) => setCategory(e.target.value as Category)}>
              {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Usually at" hint="For travel time when events have no usable address">
            <select className="input" value={placeId} onChange={(e) => setPlaceId(e.target.value)}>
              <option value="">Use event location / none</option>
              {places.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid-2">
          <Field label="Arrive early (min)" hint="e.g. 10 min before a shift">
            <NumberInput value={buffer} onChange={setBuffer} min={0} step={5} />
          </Field>
          {(isFeed || willBecomeFeed) && (
            <Field label="Check for changes every (min)">
              <NumberInput value={interval} onChange={setInterval} min={5} step={5} />
            </Field>
          )}
        </div>
        {(isFeed || willBecomeFeed) && (
          <Field label="Rename events" hint={'Optional. {title} is the original name – "Shift · {title}" turns "Bar" into "Shift · Bar".'}>
            <input className="input" value={template} onChange={(e) => setTemplate(e.target.value)} placeholder="Shift · {title}" />
          </Field>
        )}
        <Field label="Colour">
          <ColorPicker value={color} onChange={setColor} />
        </Field>
      </div>
    </Modal>
  );
}

function PlaceRules() {
  const rules = useQuery({ queryKey: ['rules'], queryFn: () => api.get<PlaceRule[]>('/place-rules') });
  const places = usePlaces().data ?? [];
  const sources = useSources().data ?? [];
  const [pattern, setPattern] = useState('');
  const [placeId, setPlaceId] = useState('');
  const [online, setOnline] = useState(false);
  const [sourceId, setSourceId] = useState('');
  const add = useAction(() => api.post('/place-rules', { pattern: pattern.trim(), placeId: placeId || null, online, sourceId: sourceId || null }), 'Rule added');
  const remove = useAction((id: string) => api.del(`/place-rules/${id}`), 'Rule removed');

  return (
    <>
      <div className="section-title" style={{ marginTop: 28 }}>
        Location rules
      </div>
      <div className="card">
        <p className="small subtle" style={{ marginBottom: 12 }}>
          Timetables use room codes like <code>32/1015</code>. Teach Tempo where they are: if an event's location contains (or matches <code>/regex/</code>) …,
          it happens at … – so travel time is right. Online events (Teams, Zoom) need no travel automatically.
        </p>
        <div className="stack tight">
          {(rules.data ?? []).map((r) => (
            <div key={r.id} className="row" style={{ fontSize: 14 }}>
              <span className="grow">
                "<b>{r.pattern}</b>" → {r.online ? 'online (no travel)' : (places.find((p) => p.id === r.placeId)?.name ?? '?')}
                {r.sourceId ? <span className="faint"> · {sources.find((s) => s.id === r.sourceId)?.name}</span> : null}
              </span>
              <Button size="sm" icon variant="ghost" onClick={() => remove.mutate(r.id)} aria-label="Remove rule">
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
        </div>
        <div className="divider" style={{ margin: '12px 0' }} />
        <div className="grid-3">
          <Field label="Location contains">
            <input className="input" value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="Boldrewood or /^(176|178)\//" />
          </Field>
          <Field label="It's at">
            <select className="input" value={online ? '__online' : placeId} onChange={(e) => (e.target.value === '__online' ? (setOnline(true), setPlaceId('')) : (setOnline(false), setPlaceId(e.target.value)))}>
              <option value="">Pick a place…</option>
              <option value="__online">Online – no travel</option>
              {places.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="For calendar">
            <select className="input" value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
              <option value="">Any</option>
              {sources.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="row" style={{ marginTop: 10, justifyContent: 'flex-end' }}>
          <Button size="sm" disabled={!pattern.trim() || (!placeId && !online)} onClick={() => add.mutateAsync().then(() => setPattern('')).catch(() => undefined)}>
            <Plus size={14} /> Add rule
          </Button>
        </div>
      </div>
    </>
  );
}
